import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  patientApi,
  FilterPatientDto,
  CreatePatientDto,
  UpdatePatientDto
} from '../services/patientApi';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/utils/errorHandling';

// Query keys factory
export const patientKeys = {
  all: ['patients'] as const,
  lists: () => [...patientKeys.all, 'list'] as const,
  list: (filters?: FilterPatientDto) => [...patientKeys.lists(), filters] as const,
  details: () => [...patientKeys.all, 'detail'] as const,
  detail: (id: string) => [...patientKeys.details(), id] as const,
  statistics: () => [...patientKeys.all, 'statistics'] as const,
};

/**
 * Hook to fetch paginated patients list with optional filters
 */
export const usePatients = (filters?: FilterPatientDto) => {
  return useQuery({
    queryKey: patientKeys.list(filters),
    queryFn: () => patientApi.getPatients(filters),
    staleTime: 30000, // Consider data fresh for 30 seconds
  });
};

/**
 * Hook to fetch a single patient by ID
 */
/**
 * `enabled` lets a caller hold the fetch until something else has seeded
 * this key — the patient chart pulls the whole chart in one request and
 * writes the patient into the cache, so firing here first would make that
 * request pointless for this one section.
 */
export const usePatient = (id: string, enabled = true) => {
  return useQuery({
    queryKey: patientKeys.detail(id),
    queryFn: () => patientApi.getPatientById(id),
    enabled: !!id && enabled,
    staleTime: 30000,
  });
};

/**
 * Hook to fetch patient statistics
 */
export const usePatientStatistics = () => {
  return useQuery({
    queryKey: patientKeys.statistics(),
    queryFn: () => patientApi.getPatientStatistics(),
    staleTime: 60000, // Statistics can be cached longer
  });
};

/**
 * Hook to create a new patient
 */
export const useCreatePatient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patientData: CreatePatientDto) => patientApi.createPatient(patientData),
    onSuccess: () => {
      // Invalidate and refetch patients list
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: patientKeys.statistics() });
      toast.success('Patient created successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to create patient');
    },
  });
};

/**
 * Hook to update an existing patient
 */
export const useUpdatePatient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, patientData }: { id: string; patientData: UpdatePatientDto }) =>
      patientApi.updatePatient(id, patientData),
    onSuccess: (_, { id }) => {
      // Invalidate specific patient and lists
      queryClient.invalidateQueries({ queryKey: patientKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: patientKeys.statistics() });

      // Surfaces that embed the patient's name (prescriptions are served with
      // the live joined name; appointments label rows from the joined patient).
      // Refresh them so a rename reflects immediately instead of on next stale.
      queryClient.invalidateQueries({ queryKey: ['prescriptions'] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });

      toast.success('Patient updated successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to update patient');
    },
  });
};

/**
 * Hook to delete a patient
 */
export const useDeletePatient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => patientApi.deletePatient(id),
    onSuccess: () => {
      // Invalidate lists and statistics
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: patientKeys.statistics() });

      toast.success('Patient deleted successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to delete patient');
    },
  });
};
